<template>
  <v-card flat>
    <v-card-title class="pb-0">
      <span class="text-subtitle-1 font-weight-bold">個人假期</span>
      <v-spacer></v-spacer>
      <v-btn
        v-if="canEditRole"
        small
        class="
          font-weight-bold
          text-capitalize
          btn-primary
          bg-gradient-primary
          py-3
          px-6
          mr-2
        "
        @click="openDialog"
      >
        <v-icon left small>mdi-plus</v-icon>
        新增
      </v-btn>
    </v-card-title>
    <v-card-text class="pt-2">
      <v-skeleton-loader v-if="loading" type="table" class="mb-4" />

      <div v-else-if="!holidays.length" class="text-center py-8">
        <v-icon size="48" color="grey lighten-1">mdi-calendar-blank</v-icon>
        <div class="mt-2 grey--text">尚未設定個人假期</div>
      </div>

      <v-simple-table v-else dense>
        <thead>
          <tr>
            <th class="text-left">日期</th>
            <th class="text-left">名稱</th>
            <th class="text-left" style="width: 120px">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in holidays" :key="item.id">
            <td>{{ item.date }}</td>
            <td>{{ item.name || "公司福利" }}</td>
            <td>
              <v-btn v-if="canEditRole" icon x-small @click="edit(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="canEditRole"
                icon
                x-small
                @click="remove(item)"
                :disabled="saving"
              >
                <v-icon small color="error">mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <div v-if="error" class="red--text text-caption mt-2">{{ error }}</div>
    </v-card-text>

    <v-dialog v-model="dialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">
          {{ editingId ? "編輯個人假期" : "新增個人假期" }}
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="form.date"
              label="日期"
              type="date"
              :rules="[rules.required]"
              dense
            />
            <v-text-field v-model="form.name" label="名稱" dense />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">取消</v-btn>
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
              mr-2
            "
            :loading="saving"
            @click="save"
            >儲存</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PersonalHolidayWidget",
  props: {
    targetUserId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      holidays: [],
      error: "",
      dialog: false,
      valid: false,
      editingId: null,
      form: {
        date: "",
        name: "公司福利",
      },
      rules: {
        required: (v) => !!v || "必填",
      },
    };
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    ...mapGetters("user", ["canEditRole"]),
    company_id() {
      return this.selectedCompany ? this.selectedCompany.id : null;
    },
  },
  watch: {
    targetUserId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchHolidays();
        }
      },
    },
  },
  methods: {
    async fetchHolidays() {
      if (!this.targetUserId) return;
      this.loading = true;
      this.error = "";
      try {
        const res = await this.$axios.get("/api/personal-holidays", {
          params: {
            user_id: this.targetUserId,
            company_id: this.company_id,
            limit: 100,
          },
        });
        this.holidays = res.data?.data || [];
      } catch (e) {
        console.error("Failed to fetch personal holidays", e);
        this.error = "無法載入個人假期";
      } finally {
        this.loading = false;
      }
    },
    openDialog() {
      if (!this.canEditRole) return;
      this.editingId = null;
      this.form = {
        date: "",
        name: "公司福利",
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
    },
    edit(item) {
      if (!this.canEditRole) return;
      this.editingId = item.id;
      this.form = {
        date: item.date,
        name: item.name || "公司福利",
      };
      this.dialog = true;
    },
    async save() {
      if (!this.canEditRole) return;
      const formRef = this.$refs.form;
      if (formRef && !(await formRef.validate())) return;
      this.saving = true;
      this.error = "";
      try {
        const payload = {
          user_id: this.targetUserId,
          date: this.form.date,
          name: this.form.name || "公司福利",
          company_id: this.company_id,
        };
        if (this.editingId) {
          await this.$axios.put(
            `/api/personal-holidays/${this.editingId}`,
            payload
          );
        } else {
          await this.$axios.post("/api/personal-holidays", payload);
        }
        this.dialog = false;
        await this.fetchHolidays();
      } catch (e) {
        console.error("Failed to save personal holiday", e);
        const msg =
          e?.response?.data?.message ||
          (this.editingId ? "更新失敗" : "建立失敗");
        this.error = msg;
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      if (!this.canEditRole) return;
      if (!confirm("確定要刪除這筆個人假期嗎？")) return;
      this.saving = true;
      this.error = "";
      try {
        await this.$axios.delete(`/api/personal-holidays/${item.id}`);
        await this.fetchHolidays();
      } catch (e) {
        console.error("Failed to delete personal holiday", e);
        this.error = "刪除失敗";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
